/* =========================================================================
   MANNA reaction game — SLIDE your basket left/right to catch the falling
   manna. Fill it fast for up to $200; a relaxed pace still banks $50.
   (Was tap-to-collect; sliding is far kinder on touch — Eric's request.)
   ========================================================================= */
import { byId, show } from '../dom';
import { ctx } from '../context';
import { fmtDelta } from '../engine';
import { sndTick, sndCorrect, sndJackpot, applause } from '../audio';
import { maybeShowTutorial, tutorialInfoButtonHTML } from '../tutorialOverlay';

export const MANNA_TARGET = 10;
const SKIP_AWARD = 5_000; // graceful $50 for passing on a break
const CATCH_HALF = 52; // px each side of the basket center that counts as a catch

export const mannaScreenHTML = /* html */ `
<div id="screenManna" class="manna hidden">
  <div class="kicker">Desert Break${tutorialInfoButtonHTML('manna')}</div>
  <h2>Manna from Heaven</h2>
  <div class="blurb">Slide your basket to catch the falling manna. <b>Quick hands earn up to $200</b> — and take your time, you'll still bank <b>$50</b>.</div>
  <div class="manna-hud">
    <div class="hud-item"><span class="hud-k">Time</span><span class="hud-v money" id="mannaTimer">0.0s</span></div>
    <div class="hud-item"><span class="hud-k">Basket</span><span class="hud-v money" id="mannaCount">0 / 10</span></div>
  </div>
  <div class="manna-field" id="mannaField">
    <div class="manna-basket" id="mannaBasket"><span class="basket-emoji">🧺</span><div class="basket-bar"><div id="basketFill"></div></div></div>
  </div>
  <div class="result" id="mannaResult"></div>
  <button class="skiplink" id="mannaSkip">Skip this break →</button>
  <div class="cta"><button class="btn" id="mannaBtn">Start Collecting</button></div>
</div>`;

let mannaActive = false;
let mannaCount = 0;
let mannaStart = 0;
let mannaSpawnIv: ReturnType<typeof setInterval> | null = null;
let mannaRaf: number | null = null;
let basketX = 0.5; // basket center as a fraction (0..1) of the field width

export function mannaReward(seconds: number): number {
  if (seconds <= 5) return 20_000; // under 5s → $200
  if (seconds >= 20) return 5_000; // 20s+ → $50
  const dollars = 200 - 10 * (seconds - 5); // linear between
  return Math.round(dollars / 5) * 5 * 100; // round to nearest $5, in cents
}

function clearFlakes(): void {
  byId('mannaField')
    .querySelectorAll('.flake')
    .forEach((f) => f.remove());
}

function updateBasket(): void {
  byId('mannaCount').textContent = `${mannaCount} / ${MANNA_TARGET}`;
  (byId('basketFill') as HTMLElement).style.width = (mannaCount / MANNA_TARGET) * 100 + '%';
}

function positionBasket(): void {
  (byId('mannaBasket') as HTMLElement).style.left = (basketX * 100).toFixed(2) + '%';
}

/** Slide the basket to follow the pointer / finger x across the field. */
function onFieldMove(e: PointerEvent): void {
  if (!mannaActive) return;
  const r = byId('mannaField').getBoundingClientRect();
  basketX = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
  positionBasket();
}

function addFieldListeners(): void {
  const field = byId('mannaField');
  field.removeEventListener('pointermove', onFieldMove); // idempotent across replays
  field.removeEventListener('pointerdown', onFieldMove);
  field.addEventListener('pointermove', onFieldMove);
  field.addEventListener('pointerdown', onFieldMove); // a tap also jumps the basket there
}

function removeFieldListeners(): void {
  const field = byId('mannaField');
  field.removeEventListener('pointermove', onFieldMove);
  field.removeEventListener('pointerdown', onFieldMove);
}

export function showManna(): void {
  mannaActive = false;
  mannaCount = 0;
  basketX = 0.5;
  updateBasket();
  positionBasket();
  clearFlakes();
  byId('mannaTimer').textContent = '0.0s';
  byId('mannaResult').innerHTML = '';
  const btn = byId<HTMLButtonElement>('mannaBtn');
  btn.textContent = 'Start Collecting';
  btn.disabled = false;
  btn.onclick = beginManna;
  show('manna');
  maybeShowTutorial('manna'); // "slide, don't tap" is the line first-timers need
}

export function beginManna(): void {
  if (mannaActive) return;
  mannaActive = true;
  mannaCount = 0;
  basketX = 0.5;
  updateBasket();
  positionBasket();
  byId('mannaResult').innerHTML = '';
  const btn = byId<HTMLButtonElement>('mannaBtn');
  btn.disabled = true;
  btn.textContent = 'Collecting…';
  mannaStart = performance.now();
  byId('mannaSkip').classList.add('show');
  addFieldListeners();
  spawnFlake();
  mannaSpawnIv = setInterval(spawnFlake, 460);
  mannaRaf = requestAnimationFrame(tickManna);
}

function spawnFlake(): void {
  if (!mannaActive) return;
  const f = document.createElement('div');
  f.className = 'flake';
  f.textContent = '🫓'; // rustic loaf, not sandwich bread
  f.style.left = 8 + Math.random() * 84 + '%';
  f.style.animationDuration = 2.2 + Math.random() * 1.0 + 's';
  // Fall the full height of the field (+ the -46px head-start) so loaves always
  // reach the basket band regardless of how tall the field renders.
  const h = byId('mannaField').clientHeight;
  f.style.setProperty('--fall', h + 46 + 'px');
  f.addEventListener('animationend', () => f.remove());
  byId('mannaField').appendChild(f);
}

function collectFlake(f: HTMLElement): void {
  if (f.dataset.done) return;
  f.dataset.done = '1';
  f.remove();
  mannaCount++;
  updateBasket();
  sndTick();
  if (mannaCount >= MANNA_TARGET) finishManna();
}

function tickManna(): void {
  if (!mannaActive) return;
  byId('mannaTimer').textContent = ((performance.now() - mannaStart) / 1000).toFixed(1) + 's';
  // Catch: any falling loaf that reaches the basket band and overlaps its x.
  const field = byId('mannaField');
  const fr = field.getBoundingClientRect();
  const basketCx = fr.left + basketX * fr.width;
  const catchTop = fr.bottom - 88; // top of the catch band (basket rides ~10px off the floor)
  field.querySelectorAll<HTMLElement>('.flake').forEach((f) => {
    if (f.dataset.done) return;
    const r = f.getBoundingClientRect();
    const cy = r.top + r.height / 2;
    const cx = r.left + r.width / 2;
    if (cy >= catchTop && cy <= fr.bottom && Math.abs(cx - basketCx) <= CATCH_HALF) {
      collectFlake(f);
    }
  });
  mannaRaf = requestAnimationFrame(tickManna);
}

function endLoop(): void {
  removeFieldListeners();
  if (mannaSpawnIv) clearInterval(mannaSpawnIv);
  mannaSpawnIv = null;
  if (mannaRaf) cancelAnimationFrame(mannaRaf);
  mannaRaf = null;
  clearFlakes();
}

function finishManna(): void {
  mannaActive = false;
  byId('mannaSkip').classList.remove('show');
  endLoop();
  const seconds = (performance.now() - mannaStart) / 1000;
  byId('mannaTimer').textContent = seconds.toFixed(1) + 's';
  const reward = mannaReward(seconds);
  ctx.engine?.awardBonus(reward, seconds.toFixed(1) + 's', 'Manna');
  ctx.updateBank();
  ctx.floatDelta(reward);
  const fast = reward >= 20_000;
  if (fast) {
    ctx.addAchievement('⚡', 'Lightning Hands', 'Filled the manna basket in under 5 seconds');
    sndJackpot();
    applause();
  } else {
    sndCorrect();
  }
  byId('mannaResult').innerHTML = `<div class="r-amt ${fast ? 'fast' : ''}">${fmtDelta(reward)}</div><div class="r-sub">Full basket in ${seconds.toFixed(1)}s${fast ? ' — lightning hands!' : ''}</div>`;
  const btn = byId<HTMLButtonElement>('mannaBtn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = beginManna;
    ctx.nextQuestion();
  };
}

export function skipManna(): void {
  if (!mannaActive) return;
  mannaActive = false;
  endLoop();
  ctx.engine?.awardBonus(SKIP_AWARD, 'a peaceful pass', 'Skip');
  ctx.updateBank();
  ctx.floatDelta(SKIP_AWARD);
  sndCorrect();
  byId('mannaResult').innerHTML = `<div class="r-amt">${fmtDelta(SKIP_AWARD)}</div><div class="r-sub">Skipped. On to the next round.</div>`;
  const btn = byId<HTMLButtonElement>('mannaBtn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = beginManna;
    ctx.nextQuestion();
  };
  byId('mannaSkip').classList.remove('show');
}
